// useLogger.ts

interface LogEntry {
  level: string;
  timestamp: string;
  context: string;
  args: any[];
}

// Module‑level plain array (in‑memory only)
let globalLogs: LogEntry[] = [];

export function useLogger(source?: string) {
  const context = source ? `[${source}]` : "[GLOBAL]";

  // Helper to safely serialize log arguments
  const safeSerialize = (args: any[]): any[] => {
    try {
      return JSON.parse(JSON.stringify(args));
    } catch (e) {
      console.error("Error serializing log arguments", e);
      return args;
    }
  };

  const logMessage = (level: string, ...args: any[]) => {
    const timestamp = new Date().toISOString();

    // Call the appropriate console method
    try {
      switch (level) {
        case "debug":
          console.debug(context, ...args);
          break;
        case "info":
        case "log":
          console.log(context, ...args);
          break;
        case "warn":
          console.warn(context, ...args);
          break;
        case "error":
          console.error(context, ...args);
          break;
      }
    } catch (e) {
      // Fallback if console fails
    }

    try {
      globalLogs.push({
        level,
        timestamp,
        context,
        args: safeSerialize(args),
      });
    } catch (e) {
      console.error("Error pushing log entry", e);
    }
  };

  const debug = (...args: any[]) => logMessage("debug", ...args);
  const info = (...args: any[]) => logMessage("info", ...args);
  const log = (...args: any[]) => logMessage("log", ...args);
  const warn = (...args: any[]) => logMessage("warn", ...args);
  const error = (...args: any[]) => logMessage("error", ...args);

  const clear = () => {
    globalLogs = [];
  };

  // Return the plain array as logs (non-reactive)
  return { debug, info, log, warn, error, clear, logs: globalLogs };
}
